import React, { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Info } from 'lucide-react';

import Loading from '../../common/Loading';
import { useGetActualDataSchemaFiles } from '../../hooks/tanstack/useActualDataHook';
import {
  useGetPreDefinedExpenses,
  useGetUserDefinedExpenses,
} from '../../hooks/tanstack/useExpenseHook';
import {
  useCustomNavigate,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import useExpenseStore from '../../hooks/zustand/useExpenseStore';
import routes from '../../routes';
import WarningDialog from '../WarningDialog';

import AddExpenseFormDialog from './AddExpenseFormDialog';
import ExpenseCollapse from './ExpenseCollapse';

type ExpenseOption = {
  label: string;
  value: number;
  type: 'default' | 'custom';
  isLocked: boolean;
};

const ExpensesTab = () => {
  const navigate = useCustomNavigate();

  const { orgId, projectId } = useGetRouteParams();

  const isOpenAddingExpense = useExpenseStore(
    (state) => state.isOpenAddingExpense
  );

  const defaultExpenseCategory = useExpenseStore(
    (state) => state.defaultExpenseCategory
  );

  const setIsOpenAddingExpense = useExpenseStore(
    (state) => state.setIsOpenAddingExpense
  );

  const { data: actualDataFiles } = useGetActualDataSchemaFiles({
    orgId,
    projectId,
  });

  const [openCreateExpenseWarningDialog, setOpenCreateExpenseWarningDialog] =
    useState(isOpenAddingExpense || false);

  const [openCreateExpenseDialog, setOpenCreateExpenseDialog] = useState<{
    open: boolean;
    group?: string;
  }>({
    open: false,
  });

  const form = useForm();

  const {
    data: predefinedExpenseSets,
    isLoading: isLoadingPredefinedExpenses,
  } = useGetPreDefinedExpenses(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: userDefinedExpenses, isLoading: isLoadingUserDefinedExpenses } =
    useGetUserDefinedExpenses(
      {
        orgId,
        projectId,
      },
      {
        enabled: !!orgId && !!projectId,
      }
    );

  const predefinedExpenses = useMemo(
    () =>
      predefinedExpenseSets?.reduce(
        (acc, cur) => {
          const group = cur.group.name;

          acc[group] = [
            ...(acc[group] ?? []),
            {
              label: cur.name,
              value: cur.id,
              isLocked: false,
              type: 'default',
            },
          ];

          return acc;
        },
        {} as Record<string, ExpenseOption[] | null>
      ),
    [predefinedExpenseSets]
  );

  const customExpenses = useMemo(
    () =>
      userDefinedExpenses?.reduce(
        (acc, cur) => {
          const group = cur.group?.name;

          acc[group] = [
            ...(acc[group] ?? []),
            {
              label: cur.name,
              value: cur.id,
              type: 'custom',
              isLocked: cur.is_locked,
            },
          ];

          return acc;
        },
        {} as Record<string, ExpenseOption[]>
      ),
    [userDefinedExpenses]
  );

  const expenses = useMemo(
    () =>
      predefinedExpenses || customExpenses
        ? Object.keys(predefinedExpenses ?? {}).reduce(
            (acc, cur) => {
              acc[cur] = [
                ...(predefinedExpenses?.[cur] ?? []),
                ...(customExpenses?.[cur] ?? []),
              ];
              return acc;
            },
            {} as Record<string, ExpenseOption[]>
          )
        : [],
    [predefinedExpenses, customExpenses]
  );

  const handleOpenCreateExpenseDialog = (group: string) => {
    if (!actualDataFiles?.find((file) => file.is_imported)) {
      setOpenCreateExpenseDialog({
        open: true,
        group,
      });
      return;
    }

    setOpenCreateExpenseDialog({
      open: false,
      group,
    });
    setOpenCreateExpenseWarningDialog(true);
  };

  const handleCloseCreateExpenseDialog = () => {
    setIsOpenAddingExpense(false);
    setOpenCreateExpenseDialog({
      open: false,
      group: undefined,
    });
  };

  const category = defaultExpenseCategory ?? openCreateExpenseDialog.group;
  const isLoading = isLoadingPredefinedExpenses || isLoadingUserDefinedExpenses;

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-4">
        <div className="flex justify-between">
          <h1 className="text-2xl font-bold">Expenses</h1>
          <WarningDialog
            open={openCreateExpenseWarningDialog}
            primaryActionText={'Continue'}
            handlePrimaryAction={() => {
              setOpenCreateExpenseWarningDialog(false);
              setOpenCreateExpenseDialog({
                open: true,
                group: openCreateExpenseDialog.group,
              });
            }}
            handleClose={() => {
              setIsOpenAddingExpense(false);
              setOpenCreateExpenseWarningDialog(false);
            }}
            handleSecondaryAction={() => {
              navigate(routes.projects.actualData, {
                org_id: orgId.toString(),
                project_id: projectId.toString(),
              });
              setIsOpenAddingExpense(false);
              setOpenCreateExpenseWarningDialog(false);
            }}
            secondaryActionText="Cancel & Download Current File"
            content={
              <p>
                Adding a custom expense will make your current{' '}
                <span>Actual Data</span> file outdated. You'll need to download
                the new templates, update your data, and upload again.{' '}
              </p>
            }
          />
          {category && (
            <AddExpenseFormDialog
              title="Add Custom Expense"
              open={openCreateExpenseDialog.open}
              defaultValues={{
                category,
              }}
              handleClose={handleCloseCreateExpenseDialog}
            />
          )}
        </div>
        <div className="flex gap-2 items-center">
          <div>
            <Info size={20} color="#666666" />
          </div>
          <p className="text-[#666666] font-normal">
            To use the newly added Custom Expense, return to your model's
            Configuration page and edit the Expense setting to apply it.
          </p>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <div>
            {expenses &&
              Object.keys(expenses).map((group) => (
                <ExpenseCollapse
                  key={group}
                  title={group}
                  group={group}
                  handleOpenCreateExpenseDialog={handleOpenCreateExpenseDialog}
                  items={(expenses as Record<string, ExpenseOption[]>)[group]}
                  isDeletable
                  isSelectable
                />
              ))}
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default ExpensesTab;
